import { motion } from 'framer-motion';
import { Gift, DollarSign, Sparkles } from 'lucide-react';
import { CopyButton } from './CopyButton';
import type { OfferStack } from '../../types/results';

interface OfferStackCardProps {
  offer: OfferStack;
}

export function OfferStackCard({ offer }: OfferStackCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-2xl overflow-hidden"
    >
      {/* Gradient border effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-500/20 via-accent-500/10 to-primary-500/20 rounded-2xl" />
      <div className="absolute inset-[1px] bg-slate-900 rounded-2xl" />

      <div className="relative p-6 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Gift className="w-7 h-7 text-white" />
            </div>
            <div>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider block mb-1">
                Your Free Appointment Offer
              </span>
              <h3 className="text-xl font-bold text-white">
                {offer.name}
              </h3>
            </div>
          </div>
          <CopyButton text={offer.rawText} variant="small" label="Copy Offer" />
        </div>

        {offer.description && (
          <p className="text-slate-400">{offer.description}</p>
        )}

        {/* Components / Deliverables */}
        {offer.components.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-slate-300 uppercase tracking-wide">
              What They Receive
            </p>
            <div className="space-y-2">
              {offer.components.map((component, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-start justify-between gap-4 p-3 rounded-xl bg-slate-800/50 border border-slate-700/30"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <Sparkles className="w-4 h-4 text-primary-400 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-200">
                        {component.name}
                      </p>
                      {component.description && (
                        <p className="text-sm text-slate-400 mt-0.5">
                          {component.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-green-400 font-semibold shrink-0">
                    <DollarSign className="w-3.5 h-3.5" />
                    {component.value.replace('$', '')}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Total and Investment */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-700/50">
          {offer.totalValue && (
            <div className="flex-1 text-center sm:text-left">
              <p className="text-xs uppercase tracking-wider text-slate-500">
                Total Value:
              </p>
              <p className="text-2xl font-bold text-slate-100">
                {offer.totalValue}
              </p>
            </div>
          )}
          <div className="flex-1 text-center sm:text-right">
            <p className="text-xs uppercase tracking-wider text-slate-500">
              Investment:
            </p>
            <p className="text-2xl font-bold text-green-400">
              {offer.investment}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
